import { useContext } from "react";
import { useNavigate } from "react-router";

import { useDialog } from "@datanac/datanac-mui-toolkit";
import { CopyAllOutlined, MoreVertOutlined } from "@mui/icons-material";
import { Button, IconButton, ListItemIcon, Menu, MenuItem } from "@mui/material";
import { AppContext } from "App";
import { BudgetIcon } from "components/Icons/Icons";
import PopupState, { bindMenu, bindToggle } from "material-ui-popup-state";
import { formatNumber, formatPrice } from "utility/formatters";

import { TilleyCard } from "../../Cards/TilleyCard";
import { DeleteIcon } from "../../Icons/Icons";
import { BudgetService } from "../BudgetService";
import { BUDGET_STATUS_ACTIVE, BUDGET_STATUS_DRAFT } from "../helpers";

export default function BudgetSummaryCard({ budget, onChange, setIsLoadingCopy, isLoading }) {
  const { globalState } = useContext(AppContext);
  const navigate = useNavigate();

  const toggleStatus = () => {
    if (isLoading) return;
    const _newBudget = { ...budget };
    _newBudget.status = budget.status === BUDGET_STATUS_ACTIVE ? BUDGET_STATUS_DRAFT : BUDGET_STATUS_ACTIVE;
    onChange?.(_newBudget);
  };

  const { component: confirmDialogComponent, actions: confirmDialogActions } = useDialog();
  const handleDeleteOnClick = () => {
    confirmDialogActions.confirm({
      title: "Delete this entry?",
      prompt: "Are you sure you want to delete this entry?",
      promptDetail: null,
      callback: () => {
        const _newBudget = { ...budget };
        _newBudget.is_active = false;
        onChange?.(_newBudget);
      },
    });
  };

  const createCopyBudget = async () => {
    setIsLoadingCopy(true);
    try {
      const { actions: budgetActions } = new BudgetService({
        apiConfig: globalState,
        year: budget?.year,
      });
      await budgetActions.cloneBudget({ budget_id: budget?.id });
      onChange?.();
    } catch (error) {
      console.error("Error cloning budget:", error);
    } finally {
      setIsLoadingCopy(false);
    }
  };

  // ---

  return (
    <>
      <TilleyCard
        data-testid={`Open Budget ${budget?.name}`}
        onClick={() => navigate(`/budget/${budget?.id}`)}
        Avatar={BudgetIcon}
        title={budget?.year}
        subtitle={budget?.name}
        action={
          <>
            <IconButton
              data-testid={`Delete Budget ${budget?.name}`}
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteOnClick();
              }}
            >
              <DeleteIcon />
            </IconButton>
            <PopupState variant="popper" popupId="chat-popper">
              {(popupState) => (
                <>
                  <IconButton
                    {...bindToggle(popupState)}
                    onClick={(e) => {
                      e.stopPropagation();
                      bindToggle(popupState).onClick(e);
                    }}
                  >
                    <MoreVertOutlined sx={{ color: "black" }} />
                  </IconButton>
                  <Menu {...bindMenu(popupState)} onClick={(e) => e.stopPropagation()}>
                    <MenuItem
                      onClick={() => {
                        createCopyBudget();
                        popupState.close();
                      }}
                    >
                      <ListItemIcon>
                        <CopyAllOutlined />
                      </ListItemIcon>
                      Create copy...
                    </MenuItem>
                  </Menu>
                </>
              )}
            </PopupState>
          </>
        }
        details={[
          {
            label: "NET INCOME",
            value: formatPrice(budget?.revenue_simple - budget?.production_cost, 0),
          },
          {
            label: "ACRES",
            value: formatNumber(budget?.reported_acreage, 0),
          },
        ]}
        footer={
          budget?.status === BUDGET_STATUS_ACTIVE ? (
            <Button
              size="small"
              variant="contained"
              color={isLoading ? "inherit" : "primary"}
              onClick={(e) => {
                e.stopPropagation();
                if (isLoading) return;
                toggleStatus();
              }}
            >
              Active
            </Button>
          ) : (
            <Button
              size="small"
              variant={isLoading ? "contained" : "outlined"}
              color="inherit"
              sx={{ color: "text.secondary" }}
              onClick={(e) => {
                e.stopPropagation();
                if (isLoading) return;
                toggleStatus();
              }}
            >
              Draft
            </Button>
          )
        }
      />
      {confirmDialogComponent}
    </>
  );
}
